import Sentry from "@similarweb/sentry-logger";

import type SignalsService from "app/pages/sales-intelligence/sub-modules/signals/models/services/SignalsService";

import type {
    ContentJsonObject,
    EnrichedContentJsonObject,
    EnrichedFiltersStateSummary,
    SignalsFiltersUIStateData,
    FiltersStateSummary,
    SignalsDeltaData,
} from "../../types/content";
import { isExistingFilterSetJsonObject, isSignalsContentJsonObject } from "../../types/content.guards";

import type { EnrichmentDeps, IMessageContentService } from "./types";

type SuccessResult<T> = { success: true; data: T };

type ErrorResult = { success: false; error: Error };

type TransformationResult<T> = SuccessResult<T> | ErrorResult;

export function createMessageContentService(signalsService: SignalsService): IMessageContentService {
    function transformSignalsDeltaToFiltersState(
        deltaData: SignalsDeltaData,
        deps: EnrichmentDeps,
    ): TransformationResult<SignalsFiltersUIStateData> {
        try {
            const { signalIds } = deltaData;

            const signals = signalsService.getSignalsByIds(signalIds, deps.signalsById);
            const signalsDTO = signalsService.createSignalsDTOsGroupedByType(signals);

            return {
                success: true,
                data: {
                    signalsFilters: signalsDTO,
                },
            };
        } catch (error) {
            return {
                success: false,
                error: new Error("Failed to transform signals delta", error),
            };
        }
    }

    function transformFiltersStateSummaryToFiltersStateData(
        summary: FiltersStateSummary,
        deps: EnrichmentDeps,
    ): TransformationResult<EnrichedFiltersStateSummary> {
        try {
            const { signalsFilters, ...restFilters } = summary;

            if (!signalsFilters || !signalsFilters.signalIds || signalsFilters.signalIds.length === 0) {
                return {
                    success: true,
                    data: restFilters,
                };
            }

            const signalsResult = transformSignalsDeltaToFiltersState(signalsFilters, deps);

            if (!signalsResult.success) {
                return {
                    success: true,
                    data: restFilters,
                };
            }

            return {
                success: true,
                data: {
                    ...restFilters,
                    signalsFilters: signalsResult.data.signalsFilters,
                },
            };
        } catch (error) {
            return {
                success: false,
                error: new Error("Failed to transform filters state summary", error),
            };
        }
    }

    function enrichSingleJsonObject(jsonObject: ContentJsonObject, deps: EnrichmentDeps): EnrichedContentJsonObject {
        if (isSignalsContentJsonObject(jsonObject)) {
            const result = transformSignalsDeltaToFiltersState(jsonObject.data, deps);

            if (!result.success) {
                Sentry.errorTracker({
                    message: `Failed to transform signals for ${jsonObject.name}`,
                    e: (result as ErrorResult).error,
                    level: "error",
                    params: {
                        extra: {
                            jsonObject,
                        },
                    },
                });

                return jsonObject as unknown as EnrichedContentJsonObject;
            }

            return {
                ...jsonObject,
                data: result.data,
            };
        }

        if (isExistingFilterSetJsonObject(jsonObject)) {
            const result = transformFiltersStateSummaryToFiltersStateData(jsonObject.data, deps);

            if (!result.success) {
                Sentry.errorTracker({
                    message: `Failed to transform transform filter set for ${jsonObject.name}`,
                    e: (result as ErrorResult).error,
                    level: "error",
                    params: {
                        extra: {
                            jsonObject,
                        },
                    },
                });

                return jsonObject as unknown as EnrichedContentJsonObject;
            }

            return {
                ...jsonObject,
                data: result.data,
            };
        }

        return jsonObject;
    }

    return {
        enrichMessageJsonObjects(jsonObjects, deps): readonly EnrichedContentJsonObject[] {
            return jsonObjects.map((obj) => enrichSingleJsonObject(obj, deps));
        },
    };
}
